import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultTimezoneSelect from './shared/vault-timezone-select.tsx';
import VaultDateRangePicker from './shared/vault-date-range-picker.tsx';
import VaultDataScopeSelect from './shared/vault-data-scope-select.tsx';
import VaultTermsInput from './shared/vault-terms-input.tsx';
import { DestUser } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

export interface VaultGmailExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultGmailExportConfig({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultGmailExportConfigProps) {
  const theme = useTheme();
  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="column" sx={{ px: 2, pb: 3 }}>
        <Stack direction="column" gap={3}>
          <DecomNotifyUsersInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
          />

          <DecomReportRecipientInput
            day={day}
            idx={idx}
            control={control}
            watch={watch}
            setValue={setValue}
            clearErrors={clearErrors}
            errors={errors}
            actionType="vaultGmailExport"
            setActionTypesWithErrors={setActionTypesWithErrors}
            destUserFields={destUserFields}
            destUserAppend={destUserAppend}
            destUserRemove={destUserRemove}
            destUserUpdate={destUserUpdate}
          />
        </Stack>
        <Divider
          sx={{
            mb: 2,
            mt: 1.5,
            borderColor: theme.palette.semantic.outline.light,
          }}
        />
        <Stack direction="column" gap={2}>
          <Stack direction="row" gap={4}>
            <VaultDataScopeSelect day={day} idx={idx} control={control} />

            <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Exclude Email Drafts:
              </Typography>
              <Controller
                name={`actions.${day}.actions.${idx}.vaultExcludeDrafts`}
                control={control}
                render={({ field }) => (
                  <Switch {...field} checked={field.value || false} />
                )}
              />
              <Tooltip title="Excludes draft emails from the export." arrow>
                <InfoOutlined fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row">
            <VaultTimezoneSelect day={day} idx={idx} control={control} />
          </Stack>
          <Stack direction="row" gap={4}>
            <VaultDateRangePicker
              day={day}
              idx={idx}
              control={control}
              errors={errors}
              label="Date Sent (optional)"
              tooltipText="Only emails sent within this date range will be exported."
            />
          </Stack>

          <VaultTermsInput
            day={day}
            idx={idx}
            control={control}
            tooltipPreset="gmailQueryTerms"
            placeholder="Query Terms"
            helperText="Example: from:username1 subject: Hello has:attachment"
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
